import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import actions from "../store/actions";
import {
  getAccessTokenExpiresIn,
  startRefreshSession,
  stopRefreshSession,
} from "../services/keycloakTokenService";
import { performLogout } from "../utils/authLogout";

function KeycloakTokenManager() {
  const auth = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    loggedIn,
    keycloakRefreshToken,
    keycloakAccessTokenExpiresAt,
    keycloakIdToken,
    username,
    email,
    profile,
  } = useSelector((state) => ({
    loggedIn: state.loggedIn,
    keycloakRefreshToken: state.keycloakRefreshToken,
    keycloakAccessTokenExpiresAt: state.keycloakAccessTokenExpiresAt,
    keycloakIdToken: state.keycloakIdToken,
    username: state.username,
    email: state.email,
    profile: state.profile,
  }));

  const authRef = useRef(auth);
  const usernameRef = useRef(username);
  authRef.current = auth;
  usernameRef.current = username;

  const oidcRefreshToken = auth.user?.refresh_token;
  const oidcExpiresAt = auth.user?.expires_at;
  const oidcIdToken = auth.user?.id_token;

  const logout = async ({ remote = false } = {}) => {
    await performLogout({
      auth: authRef.current,
      reduxLogout: () => dispatch({ type: actions.LOGOUT }),
      history,
      username: usernameRef.current,
      remote,
    });
  };

  const updateKeycloakTokens = (payload) => {
    dispatch({ type: actions.UPDATE_KEYCLOAK_TOKENS, payload });
  };

  useEffect(() => {
    if (!loggedIn || auth.isLoading) {
      return undefined;
    }

    const refreshToken = keycloakRefreshToken || oidcRefreshToken;

    // Wait for OIDC to finish restoring before treating missing tokens as logout.
    // Persisted Redux can be ready a tick before oidc-client hydrates sessionStorage.
    if (!refreshToken) {
      if (!auth.isAuthenticated && !auth.isLoading) {
        logout({ remote: false });
      }
      return undefined;
    }

    const expiresAt = keycloakAccessTokenExpiresAt || oidcExpiresAt;

    if (!keycloakRefreshToken && oidcRefreshToken && auth.user?.access_token) {
      updateKeycloakTokens({
        accessToken: auth.user.access_token,
        refreshToken: oidcRefreshToken,
        idToken: oidcIdToken || null,
        expiresIn: getAccessTokenExpiresIn(oidcExpiresAt),
      });
    } else if (oidcIdToken && !keycloakIdToken) {
      updateKeycloakTokens({
        idToken: oidcIdToken,
      });
    }

    startRefreshSession({
      refreshToken,
      expiresAt,
      profile,
      email,
      username,
      onTokensUpdated: (tokens) => {
        updateKeycloakTokens({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          ...(tokens.id_token && { idToken: tokens.id_token }),
          expiresIn: tokens.expires_in,
          ...(tokens.profile && {
            profile: tokens.profile,
            email: tokens.email,
            username: tokens.username,
          }),
        });
      },
      onError: (error) => {
        console.error("Keycloak token refresh error:", error);
        logout({ remote: false });
      },
      onRemoteLogout: () => {
        logout({ remote: true });
      },
    });

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loggedIn,
    auth.isLoading,
    auth.isAuthenticated,
    keycloakRefreshToken,
    oidcRefreshToken,
    keycloakAccessTokenExpiresAt,
    oidcIdToken,
  ]);

  const wasLoggedInRef = useRef(false);

  useEffect(() => {
    if (loggedIn) {
      wasLoggedInRef.current = true;
      return;
    }

    if (wasLoggedInRef.current) {
      stopRefreshSession();
      wasLoggedInRef.current = false;
    }
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn || !auth.error) {
      return;
    }

    // Prefer our refresh-token session over OIDC client errors.
    if (keycloakRefreshToken || oidcRefreshToken) {
      return;
    }

    console.error("Keycloak auth error:", auth.error);
    logout({ remote: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, auth.error, keycloakRefreshToken, oidcRefreshToken]);

  return null;
}

export default KeycloakTokenManager;
